/**
 * File Attachment Handlers
 * Handles undo/redo for file attachment operations
 */

import type { Operation, NodeOperation } from '@/shared/types/projectData';
import type { OperationHandler } from '../types';

/**
 * Register all file attachment operation handlers
 */
export function registerFileHandlers(
  handlers: Map<string, OperationHandler>,
  applyOp: (op: NodeOperation) => Promise<void>
): void {
  // Attach file
  handlers.set('attach-file', {
    undo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      await applyOp({
        type: 'detach-file',
        nodeId: op.nodeId,
        fileId: op.fileId,
        skipHistory: true,
        timestamp: Date.now()
      });
    },
    redo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      await applyOp({
        type: 'attach-file',
        nodeId: op.nodeId,
        fileId: op.fileId,
        fileData: op.fileData,
        skipHistory: true,
        timestamp: Date.now()
      });
    }
  });

  // Detach file
  handlers.set('detach-file', {
    undo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      await applyOp({
        type: 'attach-file',
        nodeId: op.nodeId,
        fileId: op.fileId,
        fileData: op.fileData,
        skipHistory: true,
        timestamp: Date.now()
      });
    },
    redo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      await applyOp({
        type: 'detach-file',
        nodeId: op.nodeId,
        fileId: op.fileId,
        skipHistory: true,
        timestamp: Date.now()
      });
    }
  });

  // Delete file
  handlers.set('delete-file', {
    undo: async () => {
      throw new Error('File deletion cannot be undone. Upload the original file again.');
    },
    redo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      await applyOp({
        type: 'delete-file',
        fileId: op.fileId,
        skipHistory: true,
        timestamp: Date.now()
      });
    }
  });

  // Rename file
  handlers.set('rename-file', {
    undo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      if (op.previousFileName) {
        await applyOp({
          type: 'rename-file',
          fileId: op.fileId,
          newFileName: op.previousFileName,
          skipHistory: true,
          timestamp: Date.now()
        });
      }
    },
    redo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      if (op.newFileName) {
        await applyOp({
          type: 'rename-file',
          fileId: op.fileId,
          newFileName: op.newFileName,
          skipHistory: true,
          timestamp: Date.now()
        });
      }
    }
  });
}
